import { TARGET_SAMPLE_RATE, concatBytes, pcm16leToFloat32, resampleFloat32 } from "./pcm";

const playbackWorkletUrl = "/pcm-playback-processor.js";

async function ensureWorklet(audioContext: AudioContext, url: string) {
  try {
    await audioContext.audioWorklet.addModule(url);
  } catch (error) {
    const message = error instanceof Error ? error.message : "";
    if (!/already been loaded|already registered/i.test(message)) {
      throw error;
    }
  }
}

export class AudioQueue {
  private audioContext: AudioContext;
  private node: AudioWorkletNode | null = null;
  private leftover = new Uint8Array(0);
  private ready: Promise<void>;

  constructor(audioContext: AudioContext) {
    this.audioContext = audioContext;
    this.ready = this.init();
  }

  private async init() {
    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }
    await ensureWorklet(this.audioContext, playbackWorkletUrl);
    this.node = new AudioWorkletNode(this.audioContext, "pcm-playback-processor", {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    });
    this.node.connect(this.audioContext.destination);
  }

  async addToQueue(audioData: Uint8Array) {
    await this.ready;
    const merged = concatBytes(this.leftover, audioData);
    const evenLength = merged.length - (merged.length % 2);
    this.leftover = evenLength < merged.length ? merged.subarray(evenLength) : new Uint8Array(0);
    if (evenLength < 2) return;

    const pcm24k = pcm16leToFloat32(merged.subarray(0, evenLength));
    const samples = resampleFloat32(pcm24k, TARGET_SAMPLE_RATE, this.audioContext.sampleRate);
    this.node?.port.postMessage({ type: "push", samples }, [samples.buffer]);
  }

  clear() {
    this.leftover = new Uint8Array(0);
    this.node?.port.postMessage({ type: "clear" });
  }
}
